'use client';

import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import styles from './LoginForm.module.css';
import { loginSchema } from './LoginForm.schema';
import type { FeedbackMessageBannerProps } from '../FeedbackMessageBanner/FeedbackMessageBanner.types';
import { AuthenticationIntro } from '../AuthenticationIntro/AuthenticationIntro';
import { FeedbackMessageBanner } from '../FeedbackMessageBanner/FeedbackMessageBanner';
import { LoadingOverlay } from '../../components/LoadingOverlay/LoadingOverlay';
import { Button } from '../../components/Button/Button';
import { Input } from '../../components/Input/Input';
import { StyledLink } from '../../components/StyledLink/StyledLink';

export type LoginFormValues = z.infer<typeof loginSchema>;

export type LoginFormProps = Readonly<{
  onSubmit: (data: LoginFormValues) => void | Promise<void>;
  isLoading?: boolean;
  banner?: Pick<FeedbackMessageBannerProps, 'variant' | 'message'>;
  secondaryBanner?: Pick<FeedbackMessageBannerProps, 'variant' | 'message'> | null;
  children?: React.ReactNode;
}>;

export function LoginForm({ onSubmit, isLoading = false, banner, secondaryBanner, children }: LoginFormProps) {
  const { control, handleSubmit } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const [showPassword, setShowPassword] = useState(false);
  const defaultBanner = useMemo<Pick<FeedbackMessageBannerProps, 'variant' | 'message'>>(
    () => ({ variant: 'info', message: 'Please enter your email address and password to sign in.' }),
    []
  );
  const effectiveBanner = banner ?? defaultBanner;

  return (
    <main className="bg-white p-lr-x3 p-tb-1-75 rounded-3xl flex flex-col items-center w-full relative">
      <LoadingOverlay isLoading={isLoading} fullPage={false} />
      <AuthenticationIntro title="Sign in" subtitle="To Your Account">
        <FeedbackMessageBanner
          variant={effectiveBanner.variant}
          message={effectiveBanner.message}
          reserveEmptyPlaceholder
          className={effectiveBanner.variant === 'success' ? '' : 'book primary-text-color-submain-normal'}
        />
        {secondaryBanner ? (
          <FeedbackMessageBanner
            variant={secondaryBanner.variant}
            message={secondaryBanner.message}
            reserveEmptyPlaceholder
            className="primary-text-color-submain-normal"
          />
        ) : null}
      </AuthenticationIntro>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col items-center w-full gap-6 pt-[var(--h1-5)]">
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Email *"
              placeholder="eg. user@example.com"
              className={styles.input}
              leftIcon={<i className="clara-envelope-outline" aria-hidden="true" />}
              type="email"
              field={field}
              validationMessage={error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="password"
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Password *"
              className={styles.input}
              type={showPassword ? 'text' : 'password'}
              leftIcon={<i className="clara-lock-closed-outline" aria-hidden="true" />}
              rightIcon={
                showPassword ? (
                  <i className="clara-eye-slash-outline primary-text-color-placeholder-normal" aria-hidden="true" />
                ) : (
                  <i className="clara-eye-outline primary-text-color-placeholder-normal" aria-hidden="true" />
                )
              }
              rightIconOnClick={() => setShowPassword(!showPassword)}
              rightIconAriaLabel={showPassword ? 'Hide password' : 'Show password'}
              field={field}
              validationMessage={error?.message}
            />
          )}
        />

        <Button className={styles.signInButton} type="submit">
          Sign in
        </Button>
      </form>

      <div className="flex flex-col items-center w-full mt-[0.875rem] gap-[0.875rem]">
        <StyledLink href="/request-password-reset" variant="primary" className="text-center primary-text-color-main-normal">
          Forgot Password?
        </StyledLink>

        {children}
      </div>
    </main>
  );
}
